import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { AnalyticsContext, SettingsEvent, trackEvent } from "#app/lib/analytics";
import {
  type AgeUpdateSettings,
  GetWorkspaceAgeSettingsDocument,
  UpdateWorkspaceDocument,
} from "#app/lib/data";

// Row alias over the fragment on the schema type of the same name.
export type { AgeUpdateSettings };

/** The two posting rules a Project Update is asked for. */
export type PostingRules = AgeUpdateSettings;

const DEFAULT_AGE_SETTINGS: AgeUpdateSettings = {
  __typename: "AgeUpdateSettings",
  requireReason: false,
  requireComment: false,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** The Project posting rules plus the write that flips one. */
export function useUpdatesSettings() {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.workspaceId ?? null;

  const queryVars = useMemo(() => ({ id: workspaceId! }), [workspaceId]);

  const { data, loading, error, refetch } = useQuery(GetWorkspaceAgeSettingsDocument, {
    variables: queryVars,
    skip: !workspaceId,
    fetchPolicy: "cache-and-network",
  });

  const [updateWorkspaceMutation] = useMutation(UpdateWorkspaceDocument);

  const settings = useMemo<PostingRules>(
    () => data?.workspace?.ageUpdateSettings ?? DEFAULT_AGE_SETTINGS,
    [data],
  );

  const updateSetting = useCallback(
    async (
      field: Exclude<keyof PostingRules, "__typename">,
      value: boolean,
    ) => {
      if (!workspaceId) return;
      try {
        await updateWorkspaceMutation({
          variables: {
            id: workspaceId,
            input: {
              // Both rules ride every write: the input replaces the pair, so
              // sending only the flipped one would reset its twin.
              ageUpdateSettings: {
                requireReason: settings.requireReason,
                requireComment: settings.requireComment,
                [field]: value,
              },
            },
          },
        });
        trackEvent({
          context: AnalyticsContext.Settings,
          event: SettingsEvent.Update,
          label: "posting_rules",
          data: { field, on: value },
        });
      } catch (err) {
        toastMutationError(err, "Failed to update setting");
      }
    },
    [workspaceId, settings, updateWorkspaceMutation],
  );

  return {
    settings,
    hasData: !!data?.workspace,
    isLoading: loading,
    error,
    refetch,
    updateSetting,
  };
}
